import React, { type MouseEvent, type SyntheticEvent, useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import MentionIcon from '@atlaskit/icon/glyph/editor/mention';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { UNREAD_MENTIONS_INDICATOR_TEST_ID } from '../common/constants';
import messages from './messages';

type Props = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onClick: (e: any) => void;
};

const MentionsIndicatorIcon = () => (
	<MentionIcon primaryColor={token('color.icon.accent.blue', colors.B300)} label="" size="medium" />
);

export const MentionsIndicator = ({ onClick }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const tooltipLabel = formatMessage(messages.unreadMentionsTooltip);
	const a11yLabel = formatMessage(messages.unreadMentionsA11YLabel);

	const onShowToolTip = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'mentionIndicator',
				action: 'hovered',
			}),
		);
	}, [createAnalyticsEvent]);

	const onMentionIndicatorClick = useCallback(
		(e: SyntheticEvent, _: UIAnalyticsEvent) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					actionSubject: 'mentionIndicator',
					action: 'clicked',
				}),
			);

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const mouseEvent = e as MouseEvent<HTMLElement>;
			onClick(mouseEvent);
		},
		[onClick, createAnalyticsEvent],
	);

	return (
		<Tooltip content={tooltipLabel} onShow={onShowToolTip} delay={500} position="top">
			<IconButton
				testId={UNREAD_MENTIONS_INDICATOR_TEST_ID}
				icon={MentionsIndicatorIcon}
				appearance="subtle"
				label={a11yLabel}
				onClick={onMentionIndicatorClick}
				spacing="compact"
			/>
		</Tooltip>
	);
};
